import { useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import { GetPendingInvitations } from './usePendingInvitations'

const RevokeInvitation = graphql(/* GraphQL */ `
  mutation RevokeInvitation($input: RevokeInvitationInput!) {
    revokeInvitation(input: $input) {
      ... on EmptySuccess {
        _empty
      }
      ... on NotFoundError {
        message
      }
    }
  }
`)

interface UseRevokeInvitationReturn {
  revokeInvitation: (input: {
    organizationId: string
    invitationId: string
  }) => Promise<{ success: boolean; error?: string }>
  loading: boolean
}

/**
 * `organizationId` is no longer part of the GraphQL input (the backend
 * resolves the org from the invitation), but the hook still takes it so
 * it can refetch the org-scoped `pendingInvitations` query after a
 * successful revoke.
 */
export const useRevokeInvitation = (): UseRevokeInvitationReturn => {
  const [mutate, { loading }] = useMutation(RevokeInvitation)

  const revokeInvitation = async (input: {
    organizationId: string
    invitationId: string
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data } = await mutate({
        variables: { input: { invitationId: input.invitationId } },
        refetchQueries: [
          {
            query: GetPendingInvitations,
            variables: { input: { organizationId: input.organizationId } },
          },
        ],
      })

      const result = data?.revokeInvitation
      if (!result) {
        return { success: false, error: 'Failed to revoke invitation' }
      }
      if (result.__typename === 'EmptySuccess') {
        return { success: true }
      }
      if (result.__typename === 'NotFoundError') {
        return { success: false, error: result.message }
      }
      return { success: false, error: 'Unknown error occurred' }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'An error occurred',
      }
    }
  }

  return { revokeInvitation, loading }
}
